import React, { useState } from "react";
import { Compass, ExternalLink, Plus, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext.jsx";
import { resolveScoringAppUrl } from "@/utils/apiBaseUrl.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover.jsx";

// iOS Squircle Brand SVGs
export const BookingComIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M6 6.5h4.5c1.8 0 3 .9 3 2.3 0 1.1-.7 1.8-1.7 2.1 1.3.3 2.2 1.3 2.2 2.6 0 1.6-1.3 2.5-3.3 2.5H6V6.5zm3 3.4h1.3c.7 0 1.2-.4 1.2-1s-.5-1-1.2-1H9v2zm0 4.6h1.5c.8 0 1.4-.4 1.4-1.1 0-.7-.6-1.1-1.4-1.1H9v2.2z"
      fill="#FFFFFF"
    />
    <circle cx="17.5" cy="15" r="1.5" fill="#00B4D8" />
  </svg>
);

export const TripAdvisorIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 6.5c-3.1 0-5.8 1.8-7 4.5h14c-1.2-2.7-3.9-4.5-7-4.5zm-4.5 5c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5zm9 0c-1.4 0-2.5 1.1-2.5 2.5s1.1 2.5 2.5 2.5 2.5-1.1 2.5-2.5-1.1-2.5-2.5-2.5zm-9 1.5c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1zm9 0c.6 0 1 .4 1 1s-.4 1-1 1-1-.4-1-1 .4-1 1-1z"
      fill="#FFFFFF"
    />
  </svg>
);

export const AirbnbIcon = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className}>
    <path
      d="M12 4.5c-2.1 0-3.6 1.8-2.7 4.3.8 2.5 2.7 6.4 2.7 6.4s1.9-3.9 2.7-6.4C15.6 6.3 14.1 4.5 12 4.5zm0 6.2c-.8 0-1.4-.6-1.4-1.4 0-.8.6-1.4 1.4-1.4s1.4.6 1.4 1.4c0 .8-.6 1.4-1.4 1.4z"
      fill="#FFFFFF"
    />
  </svg>
);

export default function ScoringPortalsWidget({ onLinkClick, compact = false }) {
  const { accessToken, hasScoringAccess, organizationCapabilities } = useAuth();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!hasScoringAccess) return null;

  const isHotelOrg = organizationCapabilities?.hotel !== false;

  const handleGoToHelloWorld = () => {
    if (onLinkClick) onLinkClick();
    const targetUrl = resolveScoringAppUrl(accessToken);
    window.open(targetUrl, "_blank");
  };

  const handleOpenExternal = (url) => {
    if (onLinkClick) onLinkClick();
    setMoreOpen(false);
    window.open(url, "_blank");
  };

  if (compact) {
    return (
      <div className="flex flex-col items-center justify-center py-1">
        <button
          type="button"
          onClick={handleGoToHelloWorld}
          className="relative group flex h-10 w-10 items-center justify-center rounded-2xl bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white shadow-sm hover:scale-105 hover:shadow-md active:scale-95 transition-all duration-200 border border-slate-800"
          title="Open Hello World Travel (Active SSO)"
          aria-label="Open Hello World Travel"
        >
          <Compass className="h-5 w-5 text-emerald-400 group-hover:rotate-45 transition-transform duration-300" />
          <span className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-white" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-b from-white via-slate-50/70 to-slate-100/90 p-3 shadow-sm backdrop-blur-sm transition-all hover:border-slate-300">
      {/* Decorative ambient accents */}
      <div className="pointer-events-none absolute -right-6 -top-6 h-20 w-20 rounded-full bg-emerald-500/10 blur-xl" />
      <div className="pointer-events-none absolute -left-6 -bottom-6 h-20 w-20 rounded-full bg-indigo-500/10 blur-xl" />

      {/* Section Header */}
      <div className="flex items-center justify-between mb-2.5 px-0.5">
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-slate-600 flex items-center gap-1.5">
          <Compass className="h-3.5 w-3.5 text-emerald-600 animate-spin-slow" />
          Travel & Channels
        </span>
        <span className="inline-flex items-center gap-1 text-[9px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200/80 px-2 py-0.5 rounded-full shadow-2xs">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-ping" />
          Live SSO
        </span>
      </div>

      {/* Primary Hello World App Button */}
      <button
        type="button"
        onClick={handleGoToHelloWorld}
        className={`w-full relative group overflow-hidden flex items-center justify-between rounded-xl bg-gradient-to-r from-slate-950 via-slate-900 to-indigo-950 text-white p-2.5 text-xs font-semibold shadow-md hover:shadow-xl hover:scale-[1.01] transition-all duration-200 border border-slate-800/80 ${
          isHotelOrg ? "mb-3" : "mb-0"
        }`}
        title="Open Hello World Travel & Holiday Portal (Active SSO)"
      >
        <span className="flex items-center gap-2.5 z-10">
          <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 group-hover:scale-110 group-hover:bg-emerald-500/30 transition-all duration-200">
            <Compass className="h-4 w-4" />
          </span>
          <span className="flex flex-col text-left">
            <span className="font-bold tracking-tight text-white group-hover:text-emerald-300 transition-colors text-xs">
              Hello World
            </span>
            <span className="text-[9px] text-slate-400 font-normal">
              Travel, Stays & Holidays
            </span>
          </span>
        </span>
        <ExternalLink className="h-4 w-4 text-slate-400 group-hover:text-white group-hover:translate-x-0.5 transition-all z-10" />
      </button>

      {/* iOS-Style App Icon Tiles Grid (Hotel Organizations Only) */}
      {isHotelOrg && (
        <div className="pt-0.5">
          <div className="text-[9px] font-bold text-slate-400 uppercase tracking-widest px-0.5 mb-1.5">
            External OTA Apps
          </div>

          <div className="flex items-center justify-between gap-1.5 px-0.5">
            {/* Booking.com iOS Icon */}
            <div className="flex flex-col items-center gap-1 group flex-1">
              <button
                type="button"
                onClick={() => handleOpenExternal("https://www.booking.com")}
                className="h-10 w-10 flex items-center justify-center rounded-2xl bg-[#003580] text-white shadow-sm shadow-blue-900/20 ring-1 ring-black/10 group-hover:scale-105 group-hover:shadow-md active:scale-95 transition-all duration-200"
                title="Booking.com Partner Portal"
              >
                <BookingComIcon className="h-5 w-5" />
              </button>
              <span className="text-[9px] font-bold text-slate-600 group-hover:text-blue-900 transition-colors">
                Booking
              </span>
            </div>

            {/* TripAdvisor iOS Icon */}
            <div className="flex flex-col items-center gap-1 group flex-1">
              <button
                type="button"
                onClick={() => handleOpenExternal("https://www.tripadvisor.com")}
                className="h-10 w-10 flex items-center justify-center rounded-2xl bg-[#00AF87] text-white shadow-sm shadow-emerald-900/20 ring-1 ring-black/10 group-hover:scale-105 group-hover:shadow-md active:scale-95 transition-all duration-200"
                title="TripAdvisor Partner Portal"
              >
                <TripAdvisorIcon className="h-5 w-5" />
              </button>
              <span className="text-[9px] font-bold text-slate-600 group-hover:text-emerald-900 transition-colors truncate max-w-[54px] text-center">
                TripAdvisor
              </span>
            </div>

            {/* Airbnb iOS Icon */}
            <div className="flex flex-col items-center gap-1 group flex-1">
              <button
                type="button"
                onClick={() => handleOpenExternal("https://www.airbnb.com")}
                className="h-10 w-10 flex items-center justify-center rounded-2xl bg-[#FF5A5F] text-white shadow-sm shadow-rose-900/20 ring-1 ring-black/10 group-hover:scale-105 group-hover:shadow-md active:scale-95 transition-all duration-200"
                title="Airbnb Partner Portal"
              >
                <AirbnbIcon className="h-5 w-5" />
              </button>
              <span className="text-[9px] font-bold text-slate-600 group-hover:text-rose-900 transition-colors">
                Airbnb
              </span>
            </div>

            {/* More Portals iOS Icon (+) with Popover */}
            <Popover open={moreOpen} onOpenChange={setMoreOpen}>
              <PopoverTrigger asChild>
                <div className="flex flex-col items-center gap-1 group flex-1 cursor-pointer">
                  <button
                    type="button"
                    className="h-10 w-10 flex items-center justify-center rounded-2xl bg-gradient-to-br from-slate-700 to-slate-900 text-white shadow-sm shadow-slate-900/20 ring-1 ring-black/10 group-hover:scale-105 group-hover:shadow-md active:scale-95 transition-all duration-200"
                    title="More OTA Portals & Platforms"
                  >
                    <Plus className="h-5 w-5 text-slate-200 group-hover:text-white" />
                  </button>
                  <span className="text-[9px] font-bold text-slate-600 group-hover:text-slate-900 transition-colors">
                    More
                  </span>
                </div>
              </PopoverTrigger>
              <PopoverContent
                side="top"
                align="end"
                className="w-52 rounded-2xl p-2 shadow-xl border-slate-200 bg-white/95 backdrop-blur-md space-y-1 z-50"
              >
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider px-2 py-1 border-b border-slate-100">
                  Additional Portals
                </div>

                <button
                  type="button"
                  onClick={() => handleOpenExternal("https://www.agoda.com")}
                  className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all text-left"
                >
                  <span className="h-6 w-6 rounded-lg bg-cyan-600 text-white flex items-center justify-center font-bold text-[10px]">
                    ag
                  </span>
                  <span>Agoda Partners</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenExternal("https://www.expedia.com")}
                  className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all text-left"
                >
                  <span className="h-6 w-6 rounded-lg bg-amber-500 text-white flex items-center justify-center font-bold text-[10px]">
                    Ex
                  </span>
                  <span>Expedia Partner Central</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenExternal("https://www.google.com/travel/hotels")}
                  className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all text-left"
                >
                  <span className="h-6 w-6 rounded-lg bg-blue-600 text-white flex items-center justify-center font-bold text-[10px]">
                    G
                  </span>
                  <span>Google Hotel Center</span>
                </button>

                <button
                  type="button"
                  onClick={() => handleOpenExternal("https://www.hotels.com")}
                  className="w-full flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100 hover:text-slate-900 transition-all text-left"
                >
                  <span className="h-6 w-6 rounded-lg bg-red-600 text-white flex items-center justify-center font-bold text-[10px]">
                    H
                  </span>
                  <span>Hotels.com</span>
                </button>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}
    </div>
  );
}
